import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, getDocs, query, where, orderBy, limit, updateDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, getStorage } from 'firebase/storage';
import { db, storage, auth, functions } from './firebase';
import { httpsCallable } from 'firebase/functions';
import './ProviderDetail.css';

interface Provider {
  uid: string;
  authUid?: string;
  email: string;
  name: string;
  phoneNumber?: string;
  phone?: string;
  role: string;
  status?: 'inactive' | 'pending_approval' | 'active' | 'banned';
  deposit?: number;
  totalEarnings?: number;
  createdAt: any;
  updatedAt?: any;
  address?: string;
  bio?: string;
  avatar?: string;
  profilePhotoUrl?: string;
  serviceTypes?: Array<{
    id?: string;
    subcategoryId?: string;
    name: string;
    description?: string;
    category?: string;
    isActive?: boolean;
  } | string>;
  savedAddresses?: Array<{
    id?: string;
    label?: string;
    address: string;
    isDefault?: boolean;
  } | string>;
  documents?: Array<{
    id?: string;
    name?: string;
    url: string;
    type?: string;
    uploadedAt?: any;
  } | string>;
}

interface Subcategory {
  id: string;
  name: string;
  description?: string;
  categoryId?: string;
}

interface Booking {
  id: string;
  userInfo: {
    name: string;
    email: string;
  };
  serviceItem: {
    name: string;
    price: number;
  };
  finalPrice: number;
  status: string;
  createdAt: any;
}

interface Review {
  id: string;
  bookingId: string;
  customerId: string;
  providerId: string;
  customerName: string;
  providerName: string;
  rating: number;
  comment?: string;
  createdAt: Timestamp;
  status: 'active' | 'inactive';
}

const ProviderDetail: React.FC = () => {
  const { providerId } = useParams<{ providerId: string }>();
  const navigate = useNavigate();
  const [provider, setProvider] = useState<Provider | null>(null);
  const [recentBookings, setRecentBookings] = useState<Booking[]>([]);
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState({
    totalBookings: 0,
    completedBookings: 0,
    pendingBookings: 0,
    totalRevenue: 0
  });
  
  // Inline edit states
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [isEditingStatus, setIsEditingStatus] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [editedPhone, setEditedPhone] = useState('');
  const [editedStatus, setEditedStatus] = useState<'inactive' | 'pending_approval' | 'active' | 'banned'>('pending_approval');
  const [updating, setUpdating] = useState(false);
  
  // Add new state for email editing
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [editedEmail, setEditedEmail] = useState('');
  
  // Service types editing states
  const [isEditingServiceTypes, setIsEditingServiceTypes] = useState(false);
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState<string>('');
  const [updatingServiceTypes, setUpdatingServiceTypes] = useState(false);
  
  // Documents editing states
  const [isEditingDocuments, setIsEditingDocuments] = useState(false);
  const [newDocumentName, setNewDocumentName] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [updatingDocuments, setUpdatingDocuments] = useState(false);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewStats, setReviewStats] = useState({
    averageRating: 0,
    totalReviews: 0,
    ratingCounts: {
      1: 0,
      2: 0,
      3: 0,
      4: 0,
      5: 0
    }
  });

  // Password change states
  const [isEditingPassword, setIsEditingPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    if (providerId) {
      fetchProviderDetails();
    }
  }, [providerId]);

  useEffect(() => {
    if (providerId) {
      fetchReviews();
    }
  }, [providerId]);

  // Reset editing state when provider changes
  useEffect(() => {
    setIsEditingServiceTypes(false);
    setSelectedSubcategoryId('');
    setIsEditingDocuments(false);
    setNewDocumentName('');
    setSelectedFile(null);
    if (provider) {
      setEditedName(provider.name || '');
      setEditedPhone(provider.phoneNumber || provider.phone || '');
      setEditedStatus(provider.status || 'pending_approval');
      setEditedEmail(provider.email || ''); // Add this line
    }
    setIsEditingName(false);
    setIsEditingPhone(false);
    setIsEditingStatus(false);
    setIsEditingEmail(false); // Add this line
  }, [provider?.uid]);

  const fetchProviderDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Bắt đầu tải thông tin nhà cung cấp:', providerId);

      // Fetch provider information
      console.log('Fetching provider document...');
      const providerDoc = await getDoc(doc(db, 'users', providerId!));
      if (!providerDoc.exists()) {
        console.error('Provider document does not exist:', providerId);
        setError('Không tìm thấy nhà cung cấp');
        return;
      }

      const providerRaw = providerDoc.data();
      const providerData = {
        ...providerRaw,
        uid: providerDoc.id,
        authUid: (providerRaw as any).uid
      } as Provider;
      console.log('Provider data loaded:', providerData);
      console.log('Profile photo URL:', providerData.profilePhotoUrl);
      console.log('Avatar (fallback):', providerData.avatar);
      console.log('Service types:', providerData.serviceTypes);
      console.log('Saved addresses:', providerData.savedAddresses);
      console.log('Documents:', providerData.documents);
      setProvider(providerData);

      // Fetch subcategories for service type mapping
      console.log('Fetching subcategories...');
      try {
        const subcategoriesSnapshot = await getDocs(collection(db, 'subcategories'));
        const subcategoriesData: Subcategory[] = subcategoriesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Subcategory));
        console.log('Subcategories loaded:', subcategoriesData.length);
        console.log('Subcategories data:', subcategoriesData);
        setSubcategories(subcategoriesData);
      } catch (subcategoriesError) {
        console.error('Error fetching subcategories:', subcategoriesError);
        setSubcategories([]);
      }

      // Fetch recent bookings with fallback
      console.log('Fetching bookings...');
      let bookingsData: Booking[] = [];
      
      try {
        // Try query with orderBy first
        const bookingsQuery = query(
          collection(db, 'bookings'),
          where('providerId', '==', providerId),
          orderBy('createdAt', 'desc'),
          limit(10)
        );
        console.log('Trying bookings query with orderBy...');
        const bookingsSnapshot = await getDocs(bookingsQuery);
        bookingsData = bookingsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Booking));
        console.log('Bookings with orderBy loaded:', bookingsData.length);
      } catch (bookingsError) {
        console.warn('Bookings query with orderBy failed, trying simple query:', bookingsError);
        
        // Fallback to simple query
        const simpleBookingsQuery = query(
          collection(db, 'bookings'),
          where('providerId', '==', providerId),
          limit(10)
        );
        const simpleBookingsSnapshot = await getDocs(simpleBookingsQuery);
        bookingsData = simpleBookingsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Booking));
        
        // Sort manually by createdAt
        bookingsData.sort((a, b) => {
          if (!a.createdAt && !b.createdAt) return 0;
          if (!a.createdAt) return 1;
          if (!b.createdAt) return -1;
          
          const aTime = a.createdAt?.seconds ? a.createdAt.seconds : 0;
          const bTime = b.createdAt?.seconds ? b.createdAt.seconds : 0;
          return bTime - aTime;
        });
        
        console.log('Bookings with simple query loaded:', bookingsData.length);
      }
      
      setRecentBookings(bookingsData);

      // Calculate stats from all bookings
      console.log('Calculating stats...');
      try {
        const allBookingsQuery = query(
          collection(db, 'bookings'),
          where('providerId', '==', providerId)
        );
        const allBookingsSnapshot = await getDocs(allBookingsQuery);
        
        let totalBookings = 0;
        let completedBookings = 0;
        let pendingBookings = 0;
        let totalRevenue = 0;

        allBookingsSnapshot.docs.forEach(doc => {
          const booking = doc.data();
          totalBookings++;
          
          if (booking.status === 'completed') {
            completedBookings++;
            const price = booking.finalPrice;
            if (price && !isNaN(price) && typeof price === 'number') {
              totalRevenue += price;
            }
          } else if (booking.status === 'pending') {
            pendingBookings++;
          }
        });

        console.log('Stats calculated:', { totalBookings, completedBookings, pendingBookings, totalRevenue });
        
        setStats({
          totalBookings: totalBookings || 0,
          completedBookings: completedBookings || 0,
          pendingBookings: pendingBookings || 0,
          totalRevenue: totalRevenue || 0
        });
      } catch (statsError) {
        console.warn('Failed to calculate stats, using default values:', statsError);
        setStats({
          totalBookings: 0,
          completedBookings: 0,
          pendingBookings: 0,
          totalRevenue: 0
        });
      }

      console.log('Provider details loaded successfully');

    } catch (error: any) {
      console.error('Chi tiết lỗi khi tải thông tin nhà cung cấp:', error);
      
      let errorMessage = 'Không thể tải thông tin nhà cung cấp';
      
      if (error.code === 'permission-denied') {
        errorMessage = 'Không có quyền truy cập thông tin nhà cung cấp. Vui lòng kiểm tra quyền đăng nhập.';
      } else if (error.code === 'unavailable') {
        errorMessage = 'Dịch vụ hiện không khả dụng. Vui lòng thử lại sau.';
      } else if (error.code === 'failed-precondition') {
        errorMessage = 'Cần tạo index cho database. Vui lòng liên hệ quản trị viên.';
      } else if (error.code === 'not-found') {
        errorMessage = 'Không tìm thấy nhà cung cấp này.';
      } else if (error.message) {
        errorMessage = `Lỗi: ${error.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      // Trước tiên lấy tất cả reviews của provider
      const reviewsQuery = query(
        collection(db, 'reviews'),
        where('providerId', '==', providerId),
        orderBy('createdAt', 'desc')
      );

      const reviewsSnapshot = await getDocs(reviewsQuery);
      const reviewsData: Review[] = [];

      // Get all unique customer IDs
      const customerIds = new Set<string>();
      reviewsSnapshot.docs.forEach((doc) => {
        const data = doc.data();
        if (data.customerId) customerIds.add(data.customerId);
      });

      // Fetch customer names from users collection
      const customerNames = new Map<string, string>();
      const customerPromises = Array.from(customerIds).map(async (customerId) => {
        try {
          const userDoc = await getDoc(doc(db, 'users', customerId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            customerNames.set(customerId, userData.name || 'Không xác định');
          } else {
            customerNames.set(customerId, 'Không xác định');
          }
        } catch (error) {
          console.error(`Error fetching customer ${customerId}:`, error);
          customerNames.set(customerId, 'Không xác định');
        }
      });

      await Promise.all(customerPromises);

      // Map reviews with customer names
      reviewsSnapshot.docs.forEach((doc) => {
        const data = doc.data();
        // Nếu review không có status, thêm status mặc định là 'active'
        if (!data.status) {
          const reviewRef = doc.ref;
          updateDoc(reviewRef, { status: 'active' });
        }
        reviewsData.push({
          id: doc.id,
          ...data,
          customerName: customerNames.get(data.customerId) || data.customerName || 'Không xác định',
          status: data.status || 'active' // Sử dụng 'active' làm giá trị mặc định
        } as Review);
      });

      // Lọc reviews có status là 'active'
      const activeReviews = reviewsData.filter(review => review.status === 'active');

      // Calculate review statistics
      const totalReviews = activeReviews.length;
      const ratingCounts = {
        1: 0,
        2: 0,
        3: 0,
        4: 0,
        5: 0
      };
      let ratingSum = 0;

      activeReviews.forEach(review => {
        ratingSum += review.rating;
        ratingCounts[review.rating as 1|2|3|4|5]++;
      });

      setReviews(activeReviews);
      setReviewStats({
        averageRating: totalReviews > 0 ? ratingSum / totalReviews : 0,
        totalReviews,
        ratingCounts
      });

    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Chưa cập nhật';
    
    try {
      let date: Date;
      
      if (timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      } else if (timestamp.toDate) {
        date = timestamp.toDate();
      } else if (timestamp instanceof Date) {
        date = timestamp;
      } else {
        date = new Date(timestamp);
      }
      
      return date.toLocaleDateString('vi-VN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Lỗi định dạng';
    }
  };

  const formatCurrency = (amount?: number) => {
    if (amount === undefined || amount === null || isNaN(amount)) {
      return '0 VND';
    }
    return amount.toLocaleString('vi-VN') + ' VND';
  };

  const getSubcategoryName = (serviceType: any): string => {
    // Handle string format (could be subcategoryId)
    if (typeof serviceType === 'string') {
      const subcategory = subcategories.find(sub => sub.id === serviceType);
      return subcategory?.name || serviceType;
    }
    
    // Handle object format
    if (serviceType && typeof serviceType === 'object') {
      // If subcategoryId exists, use it to lookup name
      if (serviceType.subcategoryId) {
        const subcategory = subcategories.find(sub => sub.id === serviceType.subcategoryId);
        return subcategory?.name || serviceType.name || 'Không xác định';
      }
      
      // If name exists, check if it's actually a subcategoryId
      if (serviceType.name) {
        const subcategory = subcategories.find(sub => sub.id === serviceType.name);
        if (subcategory) {
          return subcategory.name;
        }
        return serviceType.name;
      }
      
      // If id exists, check if it's a subcategoryId
      if (serviceType.id) {
        const subcategory = subcategories.find(sub => sub.id === serviceType.id);
        if (subcategory) {
          return subcategory.name;
        }
        return serviceType.id;
      }
    }
    
    return 'Không xác định';
  };

  const getStatusLabel = (status?: string) => {
    const statusMap: { [key: string]: string } = {
      'inactive': 'Không hoạt động',
      'pending_approval': 'Chờ phê duyệt',
      'active': 'Hoạt động',
      'banned': 'Bị cấm'
    };
    return status ? statusMap[status] || status : 'Chưa cập nhật';
  };

  const getStatusClass = (status?: string) => {
    const statusClassMap: { [key: string]: string } = {
      'inactive': 'status-inactive',
      'pending_approval': 'status-pending',
      'active': 'status-active',
      'banned': 'status-banned'
    };
    return status ? statusClassMap[status] || '' : 'status-unknown';
  };

  const getBookingStatusLabel = (status: string) => {
    const statusMap: { [key: string]: string } = {
      'pending': 'Chờ xử lý',
      'confirmed': 'Đã xác nhận',
      'completed': 'Hoàn thành',
      'cancelled': 'Đã hủy',
      'pending_completion': 'Chờ hoàn thành'
    };
    return statusMap[status] || status;
  };

  const getBookingStatusClass = (status: string) => {
    const statusClassMap: { [key: string]: string } = {
      'pending': 'status-pending',
      'confirmed': 'status-confirmed',
      'completed': 'status-completed',
      'cancelled': 'status-cancelled',
      'pending_completion': 'status-pending-completion'
    };
    return statusClassMap[status] || '';
  };

  const addServiceType = async () => {
    if (!selectedSubcategoryId || !provider || !providerId) return;

    try {
      setUpdatingServiceTypes(true);

      // Check if subcategory already exists
      const currentServiceTypes = provider.serviceTypes || [];
      const alreadyExists = currentServiceTypes.some(service => {
        if (typeof service === 'string') {
          return service === selectedSubcategoryId;
        }
        return service.subcategoryId === selectedSubcategoryId || service.id === selectedSubcategoryId;
      });

      if (alreadyExists) {
        alert('Loại dịch vụ này đã được thêm!');
        return;
      }

      // Add new service type as subcategory ID string
      const updatedServiceTypes = [...currentServiceTypes, selectedSubcategoryId];

      // Update Firestore
      await updateDoc(doc(db, 'users', providerId), {
        serviceTypes: updatedServiceTypes
      });

      // Update local state
      setProvider(prev => prev ? {
        ...prev,
        serviceTypes: updatedServiceTypes
      } : null);

      setSelectedSubcategoryId('');
      console.log('Service type added successfully:', selectedSubcategoryId);

    } catch (error) {
      console.error('Error adding service type:', error);
      alert('Có lỗi khi thêm loại dịch vụ. Vui lòng thử lại.');
    } finally {
      setUpdatingServiceTypes(false);
    }
  };

  const removeServiceType = async (serviceToRemove: any, index: number) => {
    if (!provider || !providerId) return;

    try {
      setUpdatingServiceTypes(true);

      const currentServiceTypes = provider.serviceTypes || [];
      const updatedServiceTypes = currentServiceTypes.filter((_, i) => i !== index);

      // Update Firestore
      await updateDoc(doc(db, 'users', providerId), {
        serviceTypes: updatedServiceTypes
      });

      // Update local state
      setProvider(prev => prev ? {
        ...prev,
        serviceTypes: updatedServiceTypes
      } : null);

      console.log('Service type removed successfully');

    } catch (error) {
      console.error('Error removing service type:', error);
      alert('Có lỗi khi xóa loại dịch vụ. Vui lòng thử lại.');
    } finally {
      setUpdatingServiceTypes(false);
    }
  };

  const getAvailableSubcategories = () => {
    const currentServiceTypes = provider?.serviceTypes || [];
    return subcategories
      .filter(subcategory => {
        return !currentServiceTypes.some(service => {
          if (typeof service === 'string') {
            return service === subcategory.id;
          }
          return service.subcategoryId === subcategory.id || service.id === subcategory.id;
        });
      })
      .sort((a, b) => a.name.localeCompare(b.name, 'vi', { sensitivity: 'base' }));
  };

  const uploadFileToStorage = async (file: File): Promise<string> => {
    try {
      setUploading(true);
      
      // Check authentication
      if (!auth.currentUser) {
        throw new Error('Bạn cần đăng nhập để upload file');
      }
      
      if (!storage) {
        throw new Error('Firebase Storage is not initialized');
      }
      
      const timestamp = Date.now();
      const documentId = `${timestamp}-${file.name}`;
      const fileName = `provider_documents/${providerId}/${documentId}`;
      console.log('Starting upload...', { 
        fileName, 
        fileSize: file.size, 
        fileType: file.type,
        userId: auth.currentUser.uid,
        providerId
      });
      
      const storageRef = ref(storage, fileName);
      
      console.log('Uploading to path:', fileName);
      const snapshot = await uploadBytes(storageRef, file);
      console.log('Upload completed, getting download URL...');
      
      const downloadURL = await getDownloadURL(snapshot.ref);
      console.log('File uploaded successfully:', downloadURL);
      return downloadURL;
    } catch (error: any) {
      console.error('Error uploading file:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        customData: error.customData,
        serverResponse: error.serverResponse
      });
      
      // More specific error messages
      if (error.code === 'storage/unauthorized') {
        throw new Error('Không có quyền upload file. Vui lòng kiểm tra quyền đăng nhập.');
      } else if (error.code === 'storage/canceled') {
        throw new Error('Upload bị hủy.');
      } else if (error.code === 'storage/unknown') {
        throw new Error('Lỗi không xác định từ server. Vui lòng thử lại.');
      } else if (error.code === 'storage/invalid-argument') {
        throw new Error('File không hợp lệ. Vui lòng chọn file khác.');
      } else {
        throw new Error(`Upload failed: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setUploading(false);
    }
  };

  const addDocument = async () => {
    if (!selectedFile || !provider || !providerId) return;

    try {
      setUpdatingDocuments(true);

      let documentUrl = '';
      let documentName = newDocumentName.trim();

      // Upload the selected file
      try {
        documentUrl = await uploadFileToStorage(selectedFile);
        if (!documentName) {
          documentName = selectedFile.name.split('.')[0]; // Use filename without extension as default name
        }
              } catch (uploadError: any) {
          console.error('Upload error caught in addDocument:', uploadError);
          alert(`Có lỗi khi upload file: ${uploadError.message || 'Vui lòng thử lại.'}`);
          return;
        }

      // Create new document object
      const newDocument = {
        id: Date.now().toString(),
        url: documentUrl,
        name: documentName,
        type: selectedFile.type.startsWith('image/') ? 'image' : 'file',
        uploadedAt: new Date()
      };

      const currentDocuments = provider.documents || [];
      const updatedDocuments = [...currentDocuments, newDocument];

      // Update Firestore
      await updateDoc(doc(db, 'users', providerId), {
        documents: updatedDocuments
      });

      // Update local state
      setProvider(prev => prev ? {
        ...prev,
        documents: updatedDocuments
      } : null);

      // Reset form
      setNewDocumentName('');
      setSelectedFile(null);
      console.log('Document added successfully:', newDocument);

    } catch (error) {
      console.error('Error adding document:', error);
      alert('Có lỗi khi thêm tài liệu. Vui lòng thử lại.');
    } finally {
      setUpdatingDocuments(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type (images only)
      if (!file.type.startsWith('image/')) {
        alert('Vui lòng chọn file hình ảnh (.jpg, .png, .gif, etc.)');
        event.target.value = '';
        return;
      }

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        alert('File quá lớn. Vui lòng chọn file nhỏ hơn 5MB.');
        event.target.value = '';
        return;
      }

      setSelectedFile(file);
    }
  };

  const removeDocument = async (documentToRemove: any, index: number) => {
    if (!provider || !providerId) return;

    try {
      setUpdatingDocuments(true);

      const currentDocuments = provider.documents || [];
      const updatedDocuments = currentDocuments.filter((_, i) => i !== index);

      // Update Firestore
      await updateDoc(doc(db, 'users', providerId), {
        documents: updatedDocuments
      });

      // Update local state
      setProvider(prev => prev ? {
        ...prev,
        documents: updatedDocuments
      } : null);

      console.log('Document removed successfully');

    } catch (error) {
      console.error('Error removing document:', error);
      alert('Có lỗi khi xóa tài liệu. Vui lòng thử lại.');
    } finally {
      setUpdatingDocuments(false);
    }
  };

  const handleUpdate = async (field: 'name' | 'phone' | 'status' | 'email') => {
    if (!provider || !providerId) return;

    try {
      setUpdating(true);
      const updates: { [key: string]: any } = {};

      switch (field) {
        case 'name':
          if (editedName.trim() === '') {
            alert('Tên không được để trống');
            return;
          }
          updates.name = editedName.trim();
          break;
        case 'email':
          if (editedEmail.trim() === '') {
            alert('Email không được để trống');
            return;
          }
          if (!editedEmail.includes('@')) {
            alert('Email không hợp lệ');
            return;
          }
          updates.email = editedEmail.trim();
          break;
        case 'phone':
          if (editedPhone.trim() === '') {
            alert('Số điện thoại không được để trống');
            return;
          }
          updates.phoneNumber = editedPhone.trim();
          break;
        case 'status':
          updates.status = editedStatus;
          break;
      }

      updates.updatedAt = new Date();
      await updateDoc(doc(db, 'users', providerId), updates);

      // Update local state
      setProvider(prev => prev ? { ...prev, ...updates } : null);

      // Reset edit mode
      switch (field) {
        case 'name':
          setIsEditingName(false);
          break;
        case 'email':
          setIsEditingEmail(false);
          break;
        case 'phone':
          setIsEditingPhone(false);
          break;
        case 'status':
          setIsEditingStatus(false);
          break;
      }

    } catch (error) {
      console.error('Error updating provider:', error);
      alert('Có lỗi khi cập nhật thông tin. Vui lòng thử lại.');
    } finally {
      setUpdating(false);
    }
  };

  const handleCancel = (field: 'name' | 'phone' | 'status' | 'email') => {
    switch (field) {
      case 'name':
        setEditedName(provider?.name || '');
        setIsEditingName(false);
        break;
      case 'email':
        setEditedEmail(provider?.email || '');
        setIsEditingEmail(false);
        break;
      case 'phone':
        setEditedPhone(provider?.phoneNumber || provider?.phone || '');
        setIsEditingPhone(false);
        break;
      case 'status':
        setEditedStatus(provider?.status || 'pending_approval');
        setIsEditingStatus(false);
        break;
    }
  };

  const handleChangePassword = async () => {
    const rawUid = String((provider as any)?.authUid || provider?.uid || providerId || '').trim();
    // Sanitize UID to allowed Firebase UID characters [A-Za-z0-9_\-]
    const effectiveUid = rawUid.replace(/[^A-Za-z0-9_\-]/g, '');
    if (!effectiveUid) {
      alert('Không xác định được UID của nhà cung cấp');
      return;
    }

    if (newPassword.length < 6) {
      alert('Mật khẩu mới phải có ít nhất 6 ký tự');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      alert('Mật khẩu mới và xác nhận mật khẩu không khớp');
      return;
    }

    try {
      setChangingPassword(true);
      const callable = httpsCallable(functions, 'setUserPassword');
      const rawPayload = { uid: effectiveUid, newPassword: String(newPassword), expectedRole: 'provider' };
      const payload = JSON.parse(JSON.stringify(rawPayload));
      console.log('Calling setUserPassword with:', { rawUid, effectiveUid, payload, types: {
        uid: typeof payload.uid,
        newPassword: typeof payload.newPassword,
        expectedRole: typeof payload.expectedRole
      }});
      await callable(payload);
      setNewPassword('');
      setConfirmNewPassword('');
      setIsEditingPassword(false);
      alert('Đã cập nhật mật khẩu mới thành công');
    } catch (err: any) {
      console.error('setUserPassword error:', { code: err?.code, message: err?.message, details: err });
      const msg = err?.message || 'Có lỗi khi thay đổi mật khẩu';
      alert(msg);
    } finally {
      setChangingPassword(false);
    }
  };

  const renderStars = (rating: number) => {
    return (
      <div className="star-rating">
        {[1, 2, 3, 4, 5].map((star) => (
          <span
            key={star}
            className={`star ${star <= rating ? 'filled' : 'empty'}`}
          >
            ★
          </span>
        ))}
      </div>
    );
  };

  const renderRatingBar = (rating: number, count: number) => {
    const percentage = reviewStats.totalReviews > 0 
      ? (count / reviewStats.totalReviews) * 100 
      : 0;

    return (
      <div className="rating-bar-row">
        <div className="rating-label">{rating} sao</div>
        <div className="rating-bar-container">
          <div 
            className="rating-bar" 
            style={{ width: `${percentage}%` }}
          />
        </div>
        <div className="rating-count">({count})</div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="provider-detail">
        <div className="page-header">
          <button className="back-button" onClick={() => navigate(-1)}>
            ← Quay lại
          </button>
          <h2>Chi tiết nhà cung cấp</h2>
        </div>
        <div className="dashboard-loading">
          <div className="loading-spinner">Đang tải dữ liệu...</div>
        </div>
      </div>
    );
  }

  if (error || !provider) {
    return (
      <div className="provider-detail">
        <div className="page-header">
          <button className="back-button" onClick={() => navigate(-1)}>
            ← Quay lại
          </button>
          <h2>Chi tiết nhà cung cấp</h2>
        </div>
        <div className="error-container">
          <div className="error-message">
            <div>
              <strong>{error || 'Không tìm thấy nhà cung cấp'}</strong>
              <br />
              <small>💡 Mở Developer Console (F12) để xem thông tin chi tiết về lỗi</small>
            </div>
            <button 
              onClick={() => {
                setError(null);
                if (providerId) {
                  fetchProviderDetails();
                }
              }}
              className="retry-button"
            >
              Thử lại
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="provider-detail">
      <div className="page-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          ← Quay lại
        </button>
        <h2>Chi tiết nhà cung cấp</h2>
      </div>

      <div className="provider-info">
        <div className="info-header">
          <div className="avatar-section">
            {provider?.profilePhotoUrl || provider?.avatar ? (
              <img 
                src={provider.profilePhotoUrl || provider.avatar} 
                alt={provider.name}
                className="provider-avatar"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = '/default-avatar.png';
                }}
              />
            ) : (
              <div className="default-avatar">
                {provider?.name?.charAt(0)?.toUpperCase() || '?'}
              </div>
            )}
          </div>

          <div className="info-section">
            <div className="info-row">
              <span className="info-label">Tên:</span>
              {isEditingName ? (
                <div className="edit-controls">
                  <input
                    type="text"
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    className="edit-input"
                    disabled={updating}
                  />
                  <div className="edit-buttons">
                    <button
                      onClick={() => handleUpdate('name')}
                      disabled={updating || editedName.trim() === ''}
                      className="save-btn"
                    >
                      {updating ? 'Đang lưu...' : 'Lưu'}
                    </button>
                    <button
                      onClick={() => handleCancel('name')}
                      disabled={updating}
                      className="cancel-btn"
                    >
                      Hủy
                    </button>
                  </div>
                </div>
              ) : (
                <div className="info-value-container">
                  <span className="info-value">{provider?.name || 'Chưa cập nhật'}</span>
                  <button
                    onClick={() => setIsEditingName(true)}
                    className="edit-btn"
                    title="Chỉnh sửa tên"
                  >
                    ✎
                  </button>
                </div>
              )}
            </div>

            <div className="info-row">
              <span className="info-label">Email:</span>
              {isEditingEmail ? (
                <div className="edit-controls">
                  <input
                    type="email"
                    value={editedEmail}
                    onChange={(e) => setEditedEmail(e.target.value)}
                    className="edit-input"
                    disabled={updating}
                  />
                  <div className="edit-buttons">
                    <button
                      onClick={() => handleUpdate('email')}
                      disabled={updating || !editedEmail.includes('@')}
                      className="save-btn"
                    >
                      {updating ? 'Đang lưu...' : 'Lưu'}
                    </button>
                    <button
                      onClick={() => handleCancel('email')}
                      disabled={updating}
                      className="cancel-btn"
                    >
                      Hủy
                    </button>
                  </div>
                </div>
              ) : (
                <div className="info-value-container">
                  <span className="info-value">{provider?.email || 'Chưa cập nhật'}</span>
                  <button
                    onClick={() => setIsEditingEmail(true)}
                    className="edit-btn"
                    title="Chỉnh sửa email"
                  >
                    ✎
                  </button>
                </div>
              )}
            </div>

            <div className="info-row">
              <span className="info-label">Số điện thoại:</span>
              {isEditingPhone ? (
                <div className="edit-controls">
                  <input
                    type="tel"
                    value={editedPhone}
                    onChange={(e) => setEditedPhone(e.target.value)}
                    className="edit-input"
                    disabled={updating}
                  />
                  <div className="edit-buttons">
                    <button
                      onClick={() => handleUpdate('phone')}
                      disabled={updating || editedPhone.trim() === ''}
                      className="save-btn"
                    >
                      {updating ? 'Đang lưu...' : 'Lưu'}
                    </button>
                    <button
                      onClick={() => handleCancel('phone')}
                      disabled={updating}
                      className="cancel-btn"
                    >
                      Hủy
                    </button>
                  </div>
                </div>
              ) : (
                <div className="info-value-container">
                  <span className="info-value">{provider?.phoneNumber || provider?.phone || 'Chưa cập nhật'}</span>
                  <button
                    onClick={() => setIsEditingPhone(true)}
                    className="edit-btn"
                    title="Chỉnh sửa số điện thoại"
                  >
                    ✎
                  </button>
                </div>
              )}
            </div>

            <div className="info-row">
              <span className="info-label">Trạng thái:</span>
              {isEditingStatus ? (
                <div className="edit-controls">
                  <select
                    value={editedStatus}
                    onChange={(e) => setEditedStatus(e.target.value as 'inactive' | 'pending_approval' | 'active' | 'banned')}
                    className="edit-select"
                    disabled={updating}
                  >
                    <option value="inactive">Không hoạt động</option>
                    <option value="pending_approval">Chờ phê duyệt</option>
                    <option value="active">Hoạt động</option>
                    <option value="banned">Bị cấm</option>
                  </select>
                  <div className="edit-buttons">
                    <button
                      onClick={() => handleUpdate('status')}
                      disabled={updating}
                      className="save-btn"
                    >
                      {updating ? 'Đang lưu...' : 'Lưu'}
                    </button>
                    <button
                      onClick={() => handleCancel('status')}
                      disabled={updating}
                      className="cancel-btn"
                    >
                      Hủy
                    </button>
                  </div>
                </div>
              ) : (
                <div className="info-value-container">
                  <span className={`status-badge ${getStatusClass(provider?.status)}`}>
                    {getStatusLabel(provider?.status)}
                  </span>
                  <button
                    onClick={() => setIsEditingStatus(true)}
                    className="edit-btn"
                    title="Chỉnh sửa trạng thái"
                  >
                    ✎
                  </button>
                </div>
              )}
            </div>

            <div className="info-row">
              <span className="info-label">Mật khẩu:</span>
              {isEditingPassword ? (
                <div className="edit-controls">
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="edit-input"
                    disabled={changingPassword}
                    placeholder="Mật khẩu mới"
                  />
                  <input
                    type="password"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    className="edit-input"
                    disabled={changingPassword}
                    placeholder="Xác nhận mật khẩu mới"
                    style={{ marginTop: '8px' }}
                  />
                  <div className="edit-buttons">
                    <button
                      onClick={handleChangePassword}
                      disabled={changingPassword || newPassword.length < 6 || newPassword !== confirmNewPassword}
                      className="save-btn"
                    >
                      {changingPassword ? 'Đang lưu...' : 'Lưu'}
                    </button>
                    <button
                      onClick={() => { setIsEditingPassword(false); setNewPassword(''); setConfirmNewPassword(''); }}
                      disabled={changingPassword}
                      className="cancel-btn"
                    >
                      Hủy
                    </button>
                  </div>
                </div>
              ) : (
                <div className="info-value-container">
                  <span className="info-value">••••••••</span>
                  <button
                    onClick={() => setIsEditingPassword(true)}
                    className="edit-btn"
                    title="Đổi mật khẩu"
                  >
                    ✎
                  </button>
                </div>
              )}
            </div>

            <div className="info-row">
              <span className="info-label">Ngày tạo:</span>
              <span className="info-value">{formatDate(provider?.createdAt)}</span>
            </div>

            <div className="info-row">
              <span className="info-label">Cập nhật lần cuối:</span>
              <span className="info-value">{formatDate(provider?.updatedAt)}</span>
            </div>

            <div className="info-row">
              <span className="info-label">Số dư:</span>
              <span className="info-value">{formatCurrency(provider?.deposit)}</span>
            </div>

            <div className="info-row">
              <span className="info-label">Tổng thu nhập:</span>
              <span className="info-value">{formatCurrency(provider?.totalEarnings)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="stats-section">
        <h4>Thống kê</h4>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value">{stats.totalBookings || 0}</div>
            <div className="stat-label">Tổng booking</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.completedBookings || 0}</div>
            <div className="stat-label">Đã hoàn thành</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.pendingBookings || 0}</div>
            <div className="stat-label">Đang chờ</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{formatCurrency(stats.totalRevenue)}</div>
            <div className="stat-label">Tổng doanh thu</div>
          </div>
        </div>
      </div>

      <div className="financial-section">
        <h4>Thông tin tài chính</h4>
        <div className="financial-grid">
          <div className="financial-item">
            <label>Số tiền ký quỹ:</label>
            <span className="amount">{formatCurrency(provider?.deposit || 0)}</span>
          </div>
          <div className="financial-item">
            <label>Tổng thu nhập:</label>
            <span className="amount">{formatCurrency(provider?.totalEarnings || 0)}</span>
          </div>
        </div>
      </div>

      <div className="service-types-section">
        <div className="section-header">
          <h4>🔧 Loại dịch vụ ({provider?.serviceTypes?.length || 0})</h4>
          <button 
            onClick={() => setIsEditingServiceTypes(!isEditingServiceTypes)}
            className="edit-service-types-btn"
            disabled={updatingServiceTypes}
          >
            {isEditingServiceTypes ? 'Hoàn thành' : 'Chỉnh sửa'}
          </button>
        </div>

        {isEditingServiceTypes && (
          <div className="add-service-type">
            <div className="add-service-controls">
              <select 
                value={selectedSubcategoryId}
                onChange={(e) => setSelectedSubcategoryId(e.target.value)}
                className="subcategory-select"
                disabled={updatingServiceTypes}
              >
                <option value="">Chọn loại dịch vụ để thêm...</option>
                {getAvailableSubcategories().map(subcategory => (
                  <option key={subcategory.id} value={subcategory.id}>
                    {subcategory.name}
                  </option>
                ))}
              </select>
              <button 
                onClick={addServiceType}
                disabled={!selectedSubcategoryId || updatingServiceTypes}
                className="add-service-btn"
              >
                {updatingServiceTypes ? 'Đang thêm...' : 'Thêm'}
              </button>
            </div>
          </div>
        )}

        {provider?.serviceTypes && provider.serviceTypes.length > 0 ? (
          <div className="service-types-grid">
            {provider.serviceTypes.map((service, index) => {
              // Handle both object and string formats
              const serviceData = typeof service === 'string' 
                ? { name: service }
                : service;
              
              // Get the subcategory name
              const subcategoryName = getSubcategoryName(service);
              
              return (
                <div key={serviceData.id || index} className="service-type-card">
                  <div className="service-header">
                    <span className="service-name">
                      {subcategoryName}
                    </span>
                    <div className="service-actions">
                      {serviceData.isActive !== undefined && (
                        <span className={`active-badge ${serviceData.isActive ? 'active' : 'inactive'}`}>
                          {serviceData.isActive ? 'Hoạt động' : 'Không hoạt động'}
                        </span>
                      )}
                      {isEditingServiceTypes && (
                        <button 
                          onClick={() => removeServiceType(service, index)}
                          disabled={updatingServiceTypes}
                          className="remove-service-btn"
                          title="Xóa loại dịch vụ"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  </div>
                  {serviceData.category && (
                    <div className="service-category">{serviceData.category}</div>
                  )}
                  {serviceData.description && (
                    <div className="service-description">{serviceData.description}</div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="no-data">
            Chưa có loại dịch vụ nào được đăng ký
            {isEditingServiceTypes && getAvailableSubcategories().length > 0 && (
              <div style={{ marginTop: '16px', fontSize: '14px', color: '#666' }}>
                Sử dụng dropdown ở trên để thêm loại dịch vụ mới
              </div>
            )}
          </div>
        )}
      </div>

      <div className="addresses-section">
        <h4>📍 Địa chỉ đã lưu ({provider?.savedAddresses?.length || 0})</h4>
        {provider?.savedAddresses && provider.savedAddresses.length > 0 ? (
          <div className="addresses-grid">
            {provider.savedAddresses.map((address, index) => {
              // Handle both object and string formats
              const addressData = typeof address === 'string' 
                ? { address: address, label: `Địa chỉ ${index + 1}` }
                : address;
              
              return (
                <div key={addressData.id || index} className="address-card">
                  <div className="address-header">
                    <span className="address-label">
                      {addressData.label || `Địa chỉ ${index + 1}`}
                    </span>
                    {addressData.isDefault && (
                      <span className="default-badge">Mặc định</span>
                    )}
                  </div>
                  <div className="address-text">{addressData.address}</div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="no-data">
            Chưa có địa chỉ nào được lưu
          </div>
        )}
      </div>

      <div className="documents-section">
        <div className="section-header">
          <h4>📄 Tài liệu & Hình ảnh ({provider?.documents?.length || 0})</h4>
          <button 
            onClick={() => setIsEditingDocuments(!isEditingDocuments)}
            className="edit-documents-btn"
            disabled={updatingDocuments}
          >
            {isEditingDocuments ? 'Hoàn thành' : 'Chỉnh sửa'}
          </button>
        </div>

        {isEditingDocuments && (
          <div className="add-document">
            <div className="add-document-controls">
              <div className="document-input-group">
                <div className="input-option">
                  <label className="input-label">Chọn file hình ảnh:</label>
                  <input 
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="document-file-input"
                    disabled={updatingDocuments || uploading}
                  />
                  {selectedFile && (
                    <div className="selected-file-info">
                      <span className="file-name">📁 {selectedFile.name}</span>
                      <span className="file-size">({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)</span>
                      <button 
                        onClick={() => setSelectedFile(null)}
                        className="clear-file-btn"
                        disabled={updatingDocuments || uploading}
                      >
                        ✕
                      </button>
                    </div>
                  )}
                </div>
                
                <input 
                  type="text"
                  value={newDocumentName}
                  onChange={(e) => setNewDocumentName(e.target.value)}
                  placeholder="Tên tài liệu (tùy chọn)"
                  className="document-name-input"
                  disabled={updatingDocuments || uploading}
                />
              </div>
              <div className="document-buttons">
                <button 
                  onClick={addDocument}
                  disabled={!selectedFile || updatingDocuments || uploading}
                  className="add-document-btn"
                >
                  {uploading ? 'Đang upload...' : updatingDocuments ? 'Đang thêm...' : 'Thêm'}
                </button>
              </div>
            </div>
          </div>
        )}

        {provider?.documents && provider.documents.length > 0 ? (
          <div className="documents-grid">
            {provider.documents.map((document, index) => {
              // Handle both object and string formats
              const documentData = typeof document === 'string' 
                ? { url: document, name: `Tài liệu ${index + 1}` }
                : document;
              
              return (
                <div key={documentData.id || index} className="document-card">
                  {isEditingDocuments && (
                    <button 
                      onClick={() => removeDocument(document, index)}
                      disabled={updatingDocuments}
                      className="remove-document-btn"
                      title="Xóa tài liệu"
                    >
                      ✕
                    </button>
                  )}
                  <div className="document-preview">
                    <img 
                      src={documentData.url} 
                      alt={documentData.name || `Tài liệu ${index + 1}`}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTEyIDJMMTMuMDkgOC4yNkwyMCA5TDEzLjA5IDE1Ljc0TDEyIDIyTDEwLjkxIDE1Ljc0TDQgOUwxMC45MSA4LjI2TDEyIDJaIiBzdHJva2U9IiM5OTkiIHN0cm9rZS13aWR0aD0iMiIgZmlsbD0iI2Y1ZjVmNSIvPgo8L3N2Zz4K';
                        target.alt = 'Không thể tải hình ảnh';
                      }}
                    />
                  </div>
                  <div className="document-info">
                    <div className="document-name">
                      {documentData.name || `Tài liệu ${index + 1}`}
                    </div>
                    {documentData.type && (
                      <div className="document-type">{documentData.type}</div>
                    )}
                    {documentData.uploadedAt && (
                      <div className="document-date">{formatDate(documentData.uploadedAt)}</div>
                    )}
                  </div>
                  <div className="document-actions">
                    <a 
                      href={documentData.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="view-document-btn"
                    >
                      Xem
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="no-data">
            Chưa có tài liệu nào được upload
            {isEditingDocuments && (
              <div style={{ marginTop: '16px', fontSize: '14px', color: '#666' }}>
                Sử dụng form ở trên để thêm tài liệu/hình ảnh mới
              </div>
            )}
          </div>
        )}
      </div>

      <div className="reviews-section">
        <div className="reviews-header">
          <h3>Đánh giá từ khách hàng</h3>
          <div className="reviews-summary">
            <div className="rating-overview">
              <div className="average-rating">
                <span className="rating-number">{reviewStats.averageRating.toFixed(1)}</span>
                <span className="rating-max">/5</span>
              </div>
              {renderStars(Math.round(reviewStats.averageRating))}
              <div className="total-reviews">
                {reviewStats.totalReviews} đánh giá
              </div>
            </div>
            <div className="rating-bars">
              {[5, 4, 3, 2, 1].map(rating => 
                renderRatingBar(rating, reviewStats.ratingCounts[rating as 1|2|3|4|5])
              )}
            </div>
          </div>
        </div>

        <div className="reviews-list">
          {reviews.length > 0 ? (
            reviews.map(review => (
              <div key={review.id} className="review-card">
                <div className="review-header">
                  <div className="reviewer-info">
                    <div className="reviewer-name">{review.customerName}</div>
                    <div className="review-date">
                      {formatDate(review.createdAt)}
                    </div>
                  </div>
                  {renderStars(review.rating)}
                </div>
                {review.comment && (
                  <div className="review-comment">
                    {review.comment}
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="no-reviews">
              Chưa có đánh giá nào
            </div>
          )}
        </div>
      </div>

      <div className="bookings-section">
        <h4>Booking gần đây ({recentBookings.length})</h4>
        {recentBookings.length > 0 ? (
          <div className="table-container">
            <table className="bookings-table">
              <thead>
                <tr>
                  <th>Khách hàng</th>
                  <th>Dịch vụ</th>
                  <th>Giá</th>
                  <th>Trạng thái</th>
                  <th>Ngày tạo</th>
                </tr>
              </thead>
              <tbody>
                {recentBookings.map((booking) => (
                  <tr key={booking.id}>
                    <td>
                      <div>
                        <div className="customer-name">{booking.userInfo?.name || 'Chưa cập nhật'}</div>
                        <div className="customer-email">{booking.userInfo?.email || 'Chưa cập nhật'}</div>
                      </div>
                    </td>
                    <td>{booking.serviceItem?.name || 'Chưa cập nhật'}</td>
                    <td className="price-cell">{formatCurrency(booking.finalPrice)}</td>
                    <td>
                      <span className={`status ${getBookingStatusClass(booking.status)}`}>
                        {getBookingStatusLabel(booking.status)}
                      </span>
                    </td>
                    <td>{formatDate(booking.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="no-data">Chưa có booking nào</div>
        )}
      </div>
    </div>
  );
};

export default ProviderDetail; 